/**
 * Client-side card filtering for the detail-page tabs (location / plan / masterplan).
 *
 * Each tab (Floorplans / Quick Move-Ins / Model Homes) has its own Filters modal that
 * shows / hides / sorts its cards in-browser -- nothing posts to the server. The shared
 * Filters button opens whichever modal matches the active tab. Filter values carry
 * ORM-ish prefixes (`@` = "N+", `%` = range), stripped before comparing.
 *
 * The active tab's filters, the page-level sort, and the active tab are mirrored into the
 * URL query string (readable + shareable) and restored on load. Enqueued from
 * ``DetailFilterMixin`` (see keystone2/view/detail_filters.py), so it only loads on the
 * three detail views that render ``inc_card_tabs.html`` with ``detail_filters=True``.
 */

/**
 * @typedef {object} TabConfig
 * @property {string} tabId - Pane id, e.g. `"nav-specs"`.
 * @property {string} [formId] - The tab's Filters form id.
 * @property {string} modalId - The tab's Filters modal id.
 * @property {string} [noResultsId] - The tab's empty-state element id.
 * @property {string} [loadMoreId] - The tab's load-more button id.
 * @property {string[]} [resetIds] - Element ids that reset this tab's filters.
 */

/**
 * @typedef {object} TabController
 * @property {string} tabId
 * @property {string} modalId
 * @property {HTMLFormElement|null} form
 * @property {() => void} apply - Re-filter, sort, and re-render this tab's cards.
 * @property {() => void} reset - Clear this tab's controls and re-apply.
 * @property {string[]} resetIds
 */

/**
 * Applied filter values, keyed by control name. Bed/bath/sqft are single strings;
 * stories/collection are arrays gathered from checkbox groups.
 * @typedef {{ bedrooms?: string, bathrooms?: string, square_footage?: string, stories?: string[], collection?: string[] }} Filters
 */

/** @typedef {{ reset: () => void, update: (opts: { from: number, to: number }) => void }} IonRangeSlider */
/** @typedef {HTMLInputElement & { oiSlider?: IonRangeSlider }} SliderInput */
/** @typedef {{ el: Element, card: Element|null, index: number }} CardItem */

/**
 * Wire up every filterable tab on the page, bind the chrome (Filters button, sort,
 * chips), and restore any filter/sort state carried in the URL.
 *
 * @returns {void}
 */
function initDetailFilters() {
    const sortSelect = /** @type {HTMLSelectElement} */ (document.getElementById("oi-sort-select"));
    const sortWrapper = document.querySelector(".sort-wrapper");
    const badgeContainer = document.getElementById("filter-chips");
    const filterButtons = Array.from(document.querySelectorAll(".btn-filter-menu"));

    /**
     * Cards shown before the load-more cutoff, sized to the viewport.
     * @returns {number}
     */
    function getLimit() {
        if (window.innerWidth >= 1200) {
            return 8;
        }
        if (window.innerWidth >= 768) {
            return 6;
        }
        return 3;
    }

    /**
     * Drop a filter value's ORM prefix (`@`, `%`, etc.) for numeric comparison.
     * @param {string} value
     * @returns {string}
     */
    function stripPrefix(value) {
        return value.replace(/^[@=~<>%]/, "");
    }

    /**
     * Collect the active filters from a tab's form: selects + the sqft slider's hidden
     * input, single-select bed/bath radios, and multi-select stories/collection checkboxes.
     * @param {HTMLFormElement|null} form
     * @returns {Filters}
     */
    function activeFilters(form) {
        /** @type {Filters} */
        const filters = {};
        if (!form) {
            return filters;
        }
        form.querySelectorAll("select[name]").forEach((select) => {
            if (select.value) {
                filters[select.name] = select.value;
            }
        });
        // The slider writes its range into a hidden square_footage input.
        const sqft = form.querySelector('input[name="square_footage"]');
        if (sqft && sqft.value) {
            filters.square_footage = sqft.value;
        }
        // Bed/bath are single-select radio pills; Stories and More-Filters are
        // multi-select checkbox pills gathered into arrays under their shared name.
        form.querySelectorAll('input[type="radio"][name]:checked').forEach((radio) => {
            if (radio.value) {
                filters[radio.name] = radio.value;
            }
        });
        form.querySelectorAll('input[type="checkbox"][name]:checked').forEach((checkbox) => {
            (filters[checkbox.name] = filters[checkbox.name] || []).push(checkbox.value);
        });
        return filters;
    }

    /**
     * Test a card's data attributes against the active filters.
     * @param {Element|null} card
     * @param {Filters} filters
     * @returns {boolean}
     */
    function matches(card, filters) {
        if (!card) {
            return false;
        }
        if (filters.bedrooms) {
            const minBeds = parseInt(stripPrefix(filters.bedrooms), 10) || 0;
            if ((parseInt(card.getAttribute("data-bedrooms"), 10) || 0) < minBeds) {
                return false;
            }
        }
        if (filters.bathrooms) {
            const minBaths = parseFloat(stripPrefix(filters.bathrooms)) || 0;
            if ((parseFloat(card.getAttribute("data-bathrooms")) || 0) < minBaths) {
                return false;
            }
        }
        if (filters.square_footage) {
            const parts = stripPrefix(filters.square_footage).split("-");
            const lo = parseInt(parts[0], 10) || 0;
            const hi = parseInt(parts[1], 10) || Infinity;
            const cardMin = parseInt(card.getAttribute("data-square-feet"), 10) || 0;
            const cardMax = parseInt(card.getAttribute("data-square-feet-max"), 10) || cardMin;
            if (!(cardMin <= hi && cardMax >= lo)) {
                return false;
            }
        }
        if (filters.stories && filters.stories.length) {
            // Ranch = single story, Multi-Level = 2+; either selected passes.
            const stories = parseFloat(card.getAttribute("data-stories")) || 0;
            const ranch = filters.stories.includes("ranch") && stories === 1;
            const multiLevel = filters.stories.includes("multi") && stories > 1;
            if (!ranch && !multiLevel) {
                return false;
            }
        }
        if (filters.collection && filters.collection.length) {
            // More-Filters toggles: the card must carry every selected token.
            const tokens = (card.getAttribute("data-collection") || "").trim().split(/\s+/);
            if (!filters.collection.every((token) => tokens.includes(token))) {
                return false;
            }
        }
        return true;
    }

    /**
     * Build a controller for one filterable tab, bound to its own Filters form/modal.
     * Returns null when the tab isn't on the page (the caller prunes those).
     * @param {TabConfig} config
     * @returns {TabController|null}
     */
    function createTab(config) {
        const tab = document.getElementById(config.tabId);
        if (!tab) {
            return null;
        }
        const form = config.formId ? /** @type {HTMLFormElement} */ (document.getElementById(config.formId)) : null;
        const row = tab.querySelector(".row");
        const noResults = config.noResultsId ? document.getElementById(config.noResultsId) : null;
        const loadMoreWrapper = tab.querySelector(".load-more-wrapper");
        const loadMoreBtn = config.loadMoreId ? document.getElementById(config.loadMoreId) : null;
        const anchor = noResults || loadMoreWrapper || null;
        let expanded = false;

        const items = Array.from(row.querySelectorAll("[data-card-index]")).map((el) => ({
            el,
            card: el.querySelector(".card"),
            index: parseInt(el.getAttribute("data-card-index"), 10) || 0,
        }));

        /**
         * Order matched items by the page-level sort select (stable index fallback).
         * @param {CardItem[]} matched
         * @returns {CardItem[]}
         */
        function sortItems(matched) {
            const value = sortSelect ? sortSelect.value : "";
            const sorted = matched.slice();
            if (value === "name") {
                sorted.sort((a, b) =>
                    (a.card.getAttribute("data-name") || "").localeCompare(b.card.getAttribute("data-name") || ""),
                );
            } else if (value === "price") {
                sorted.sort(
                    (a, b) =>
                        (parseInt(a.card.getAttribute("data-price"), 10) || 0) -
                        (parseInt(b.card.getAttribute("data-price"), 10) || 0),
                );
            } else if (value === "-price") {
                sorted.sort(
                    (a, b) =>
                        (parseInt(b.card.getAttribute("data-price"), 10) || 0) -
                        (parseInt(a.card.getAttribute("data-price"), 10) || 0),
                );
            } else if (value === "sqft") {
                sorted.sort(
                    (a, b) =>
                        (parseInt(a.card.getAttribute("data-square-feet"), 10) || 0) -
                        (parseInt(b.card.getAttribute("data-square-feet"), 10) || 0),
                );
            } else if (value === "-sqft") {
                // High to low reads the top of the card's range; spec cards have no max, so fall back
                /** @param {CardItem} item */
                const maxSqft = (item) =>
                    parseInt(
                        item.card.getAttribute("data-square-feet-max") || item.card.getAttribute("data-square-feet"),
                        10,
                    ) || 0;
                sorted.sort((a, b) => maxSqft(b) - maxSqft(a));
            } else {
                sorted.sort((a, b) => a.index - b.index);
            }
            return sorted;
        }

        /**
         * Move an item into place, before the no-results / load-more anchor when present.
         * @param {CardItem} item
         * @returns {void}
         */
        function place(item) {
            if (anchor) {
                row.insertBefore(item.el, anchor);
            } else {
                row.appendChild(item.el);
            }
        }

        /**
         * Hide matched items past the viewport limit and toggle the load-more button.
         * @param {CardItem[]} matched
         * @returns {void}
         */
        function applyShowMore(matched) {
            const limit = getLimit();
            let hasHidden = false;
            matched.forEach((item, i) => {
                if (!expanded && i >= limit) {
                    item.el.classList.add("d-none");
                    hasHidden = true;
                } else {
                    item.el.classList.remove("d-none");
                }
            });
            if (loadMoreWrapper) {
                loadMoreWrapper.style.display = hasHidden || expanded ? "" : "none";
            }
            if (loadMoreBtn) {
                loadMoreBtn.textContent = expanded ? "Show Less" : "Load More";
            }
        }

        /**
         * Update the tab button's `(N)` count.
         * @param {number} count
         * @returns {void}
         */
        function updateTabCount(count) {
            document.querySelectorAll(`[data-bs-target="#${config.tabId}"]`).forEach((el) => {
                el.textContent = el.textContent.replace(/\(\d+\)/, `(${count})`);
            });
        }

        /**
         * Filter, sort, re-order, and re-limit this tab's cards.
         * @returns {void}
         */
        function apply() {
            const filters = activeFilters(form);
            const matched = sortItems(items.filter((item) => matches(item.card, filters)));
            const matchedSet = new Set(matched.map((item) => item.el));
            matched.forEach(place);
            items.forEach((item) => {
                if (!matchedSet.has(item.el)) {
                    item.el.classList.add("d-none");
                    place(item);
                }
            });
            applyShowMore(matched);
            updateTabCount(matched.length);
            if (noResults) {
                noResults.classList.toggle("d-none", matched.length > 0);
            }
        }

        /**
         * Clear this tab's controls back to their defaults, then re-apply.
         * @returns {void}
         */
        function reset() {
            expanded = false;
            if (form) {
                form.querySelectorAll("select[name]").forEach((sel) => {
                    sel.selectedIndex = 0;
                });
                form.querySelectorAll('input[type="checkbox"][name]').forEach((cb) => {
                    cb.checked = false;
                });
                // Radio pill groups: re-check the "Any" option (value "").
                form.querySelectorAll('input[type="radio"][name]').forEach((radio) => {
                    radio.checked = radio.value === "";
                });
                // Sliders: restore the full range and clear the hidden value.
                form.querySelectorAll('input[name="square_footage"]').forEach((hidden) => {
                    if (hidden.oiSlider) {
                        hidden.oiSlider.reset();
                    }
                    hidden.value = "";
                });
            }
            apply();
        }

        if (loadMoreBtn) {
            loadMoreBtn.addEventListener("click", (e) => {
                e.preventDefault();
                expanded = !expanded;
                apply();
            });
        }

        return {
            tabId: config.tabId,
            modalId: config.modalId,
            form,
            apply,
            reset,
            resetIds: config.resetIds || [],
        };
    }

    /** @type {Record<string, TabController>} */
    const controllers = {};
    // Attempt every tab unconditionally; createTab returns null for tabs not on this
    // page (a location has specs/models, a plan/masterplan has floorplans/specs/models),
    // and only non-null controllers are registered. Replaces the old per-tab Django {% if %} guards.
    const tabConfigs = [
        {
            tabId: "nav-floorplans",
            formId: "oi-filter-form-floorplans",
            modalId: "filters-modal-floorplans",
            noResultsId: "floorplans-no-results",
            loadMoreId: "floorplans-load-more",
            resetIds: ["floorplans-modal-reset", "floorplans-filter-reset"],
        },
        {
            tabId: "nav-specs",
            formId: "oi-filter-form-specs",
            modalId: "filters-modal-specs",
            noResultsId: "specs-no-results",
            loadMoreId: "specs-load-more",
            resetIds: ["specs-modal-reset", "specs-filter-reset"],
        },
        {
            tabId: "nav-models",
            formId: "oi-filter-form-models",
            modalId: "filters-modal-models",
            noResultsId: "models-no-results",
            loadMoreId: "models-load-more",
            resetIds: ["models-modal-reset", "models-filter-reset"],
        },
    ];
    tabConfigs.forEach((config) => {
        const controller = createTab(config);
        if (controller) {
            controllers[config.tabId] = controller;
        }
    });

    // Nothing filterable on this page -- bail before binding chrome.
    if (!Object.keys(controllers).length) {
        return;
    }

    // Scope strictly to the card tabs: the page also hosts oilib's login/register tab
    // widget, so an unscoped ".tab-pane.show.active" query matches its pane and blanks the chrome.
    const CARD_TAB_IDS = ["nav-floorplans", "nav-specs", "nav-models"];

    /**
     * The controller for the currently-shown card tab, or null.
     * @returns {TabController|null}
     */
    function currentController() {
        for (const id of CARD_TAB_IDS) {
            const pane = document.getElementById(id);
            if (pane && pane.classList.contains("show") && pane.classList.contains("active")) {
                return controllers[id] || null;
            }
        }
        return null;
    }

    /**
     * Update the Filters button's active-filter count pill.
     * @param {number} count
     * @returns {void}
     */
    function updateFilterBadge(count) {
        filterButtons.forEach((btn) => {
            const badge = btn.querySelector(".badge");
            if (badge) {
                badge.textContent = String(count);
                // Hide the pill entirely when nothing's applied -- a bare "0" reads as noise.
                badge.classList.toggle("d-none", !count);
            }
        });
    }

    // Bed/bath pills share the same "3+" label text, so tag the chip with its category.
    const PILL_CATEGORY = { bedrooms: "Beds", bathrooms: "Baths" };

    /**
     * The visible label for a pill input (its associated <label>, else the raw value).
     * @param {HTMLInputElement} input
     * @returns {string}
     */
    function pillLabel(input) {
        const label = input.labels && input.labels[0];
        return label ? label.textContent.trim() : input.value;
    }

    /**
     * Append a filter chip with a clear (x) button.
     * @param {string} text
     * @param {() => void} onClear - Reset the chip's own control; syncChrome re-renders.
     * @returns {void}
     */
    function addBadge(text, onClear) {
        // Same chip markup as oifilter.js's renderFilterBadges, so the _maps.scss chip
        // styles and the include's `badge_class` apply here too.
        const badgeClass = badgeContainer.dataset.badgeClass || "";
        const badge = document.createElement("span");
        badge.className = "filter-chip" + (badgeClass ? ` ${badgeClass}` : "");
        badge.textContent = `${text} `;
        const clear = document.createElement("button");
        clear.type = "button";
        clear.className = "clear-filter";
        clear.setAttribute("aria-label", "Remove filter");
        clear.innerHTML = '<i class="fa-light fa-x"></i>';
        clear.addEventListener("click", () => {
            onClear();
            syncChrome();
        });
        badge.appendChild(clear);
        badgeContainer.appendChild(badge);
    }

    /**
     * Render a dismissable chip per active control. The detail-page filters are pills +
     * a slider (no <select>), so this mirrors activeFilters(); each chip clears just its
     * own control, then re-applies and re-syncs.
     * @param {TabController|null} controller
     * @returns {void}
     */
    function renderBadges(controller) {
        if (!badgeContainer) {
            return;
        }
        badgeContainer.innerHTML = "";
        const form = controller && controller.form;
        if (!form) {
            return;
        }
        // Bed/bath: one chip for the checked pill; skip the "Any" option (value "").
        form.querySelectorAll('input[type="radio"][name]:checked').forEach((el) => {
            const radio = /** @type {HTMLInputElement} */ (el);
            if (!radio.value) {
                return;
            }
            const category = PILL_CATEGORY[radio.name];
            addBadge(category ? `${pillLabel(radio)} ${category}` : pillLabel(radio), () => {
                const any = form.querySelector(`input[name="${radio.name}"][value=""]`);
                if (any) {
                    any.checked = true;
                } else {
                    radio.checked = false;
                }
                controller.apply();
            });
        });
        // Stories + More Filters: one chip per checked toggle.
        form.querySelectorAll('input[type="checkbox"][name]:checked').forEach((el) => {
            const checkbox = /** @type {HTMLInputElement} */ (el);
            addBadge(pillLabel(checkbox), () => {
                checkbox.checked = false;
                controller.apply();
            });
        });
        // Square footage slider (hidden "%{from}-{to}" input).
        const sqft = /** @type {SliderInput} */ (form.querySelector('input[name="square_footage"]'));
        if (sqft && sqft.value) {
            const [lo, hi] = sqft.value.replace(/^%/, "").split("-");
            addBadge(`Square Feet: ${parseInt(lo, 10).toLocaleString()} – ${parseInt(hi, 10).toLocaleString()}`, () => {
                if (sqft.oiSlider) {
                    sqft.oiSlider.reset();
                }
                sqft.value = "";
                controller.apply();
            });
        }
    }

    // --- URL sync ---------------------------------------------------------
    // These filters are client-side, so mirror the active tab's state into the query
    // string (readable + shareable) and restore it on load. replaceState keeps pill
    // toggles out of the back-button history. Writes stay off until the initial restore
    // runs, so an incoming URL isn't clobbered before it's read.
    let urlReady = false;
    const URL_KEYS = ["tab", "beds", "baths", "sqft", "stories", "features", "sort"];

    /**
     * The URL param for a tab id (`"nav-specs"` -> `"specs"`).
     * @param {string} tabId
     * @returns {string}
     */
    function tabParam(tabId) {
        return tabId.replace("nav-", "");
    }

    /**
     * Serialize the active tab's filters + page sort to the query string; a filterless,
     * unsorted page clears our keys so it keeps a clean URL. Unrelated params (utm_*,
     * etc.) ride through untouched.
     * @param {TabController|null} controller
     * @returns {void}
     */
    function writeUrl(controller) {
        if (!urlReady) {
            return;
        }
        const params = new URLSearchParams(window.location.search);
        URL_KEYS.forEach((key) => params.delete(key));
        const filters = controller && controller.form ? activeFilters(controller.form) : {};
        // activeFilters only sets a key when its control has a value, so any key means a live filter.
        if (Object.keys(filters).length > 0) {
            params.set("tab", tabParam(controller.tabId));
            if (filters.bedrooms) {
                params.set("beds", stripPrefix(filters.bedrooms));
            }
            if (filters.bathrooms) {
                params.set("baths", stripPrefix(filters.bathrooms));
            }
            if (filters.square_footage) {
                params.set("sqft", stripPrefix(filters.square_footage));
            }
            if (filters.stories && filters.stories.length) {
                params.set("stories", filters.stories.join(","));
            }
            if (filters.collection && filters.collection.length) {
                params.set("features", filters.collection.join(","));
            }
        }
        // Sort is a single page-level control, not per tab, so it rides along on its own.
        const sort = sortSelect ? sortSelect.value : "";
        if (sort) {
            params.set("sort", sort);
        }
        const query = params.toString();
        const next = window.location.pathname + (query ? `?${query}` : "") + window.location.hash;
        window.history.replaceState(null, "", next);
    }

    /**
     * Re-check the pills / move the slider to match the query string. Setting .checked
     * programmatically fires no change event, so this won't trigger a premature URL
     * write -- the caller applies + syncs explicitly.
     * @param {HTMLFormElement} form
     * @param {URLSearchParams} params
     * @returns {void}
     */
    function applyParamsToForm(form, params) {
        const checkRadio = (name, value) => {
            if (!value) {
                return;
            }
            const el = form.querySelector(`input[name="${name}"][value="${value}"]`);
            if (el) {
                el.checked = true;
            }
        };
        checkRadio("bedrooms", params.get("beds") ? `@${params.get("beds")}` : "");
        checkRadio("bathrooms", params.get("baths") ? `@${params.get("baths")}` : "");
        const sqftRaw = params.get("sqft");
        const sqft = /** @type {SliderInput} */ (form.querySelector('input[name="square_footage"]'));
        if (sqft && sqftRaw) {
            sqft.value = `%${sqftRaw}`;
            const [from, to] = sqftRaw.split("-");
            if (sqft.oiSlider && from && to) {
                sqft.oiSlider.update({ from: parseInt(from, 10), to: parseInt(to, 10) });
            }
        }
        const checkGroup = (name, csv) => {
            if (!csv) {
                return;
            }
            csv.split(",").forEach((value) => {
                const el = form.querySelector(`input[name="${name}"][value="${value}"]`);
                if (el) {
                    el.checked = true;
                }
            });
        };
        checkGroup("stories", params.get("stories"));
        checkGroup("collection", params.get("features"));
    }

    /**
     * Restore filter + sort state from a shared/bookmarked URL, then arm URL writes.
     * A hand-mangled query value can make a querySelector below throw; catch it so a bad
     * URL just aborts restore, and arm writes in finally either way so a throw can't
     * leave syncing dead for the session.
     * @returns {void}
     */
    function restoreFromUrl() {
        try {
            const params = new URLSearchParams(window.location.search);
            const hasFilters = ["beds", "baths", "sqft", "stories", "features"].some((key) => params.get(key));
            const sort = params.get("sort");
            if (!hasFilters && !sort) {
                return;
            }
            if (sort && sortSelect) {
                sortSelect.value = sort;
            }
            const requested = params.get("tab") ? `nav-${params.get("tab")}` : null;
            const controller = (requested && controllers[requested]) || currentController();
            if (!controller || !controller.form) {
                return;
            }
            if (hasFilters) {
                applyParamsToForm(controller.form, params);
                const trigger = document.querySelector(`.nav-tabs [data-bs-target="#${controller.tabId}"]`);
                if (trigger && !trigger.classList.contains("active")) {
                    trigger.click();
                }
            }
            controller.apply();
            syncChrome(controller.tabId);
        } catch {
            // Malformed filter param in the URL -- leave the page unfiltered.
        } finally {
            urlReady = true;
        }
    }

    /**
     * Point the Filters button at the active tab's modal, show the sort control only for
     * tabs with a controller (both hide where there's no filter/sort), then refresh the
     * chips, count, and URL.
     * @param {string} [paneId] - Explicit pane id from shown.bs.tab; detected otherwise.
     * @returns {void}
     */
    function syncChrome(paneId) {
        // shown.bs.tab passes the pane id explicitly: Bootstrap's fade classes lag the event,
        // so DOM detection can miss it mid-transition. Fall back to detection for settled calls.
        const controller = paneId ? controllers[paneId] || null : currentController();
        filterButtons.forEach((btn) => {
            if (controller) {
                btn.style.display = "";
                btn.setAttribute("data-bs-target", `#${controller.modalId}`);
            } else {
                btn.style.display = "none";
            }
        });
        if (sortWrapper) {
            sortWrapper.style.display = controller ? "" : "none";
        }
        renderBadges(controller);
        updateFilterBadge(controller ? Object.keys(activeFilters(controller.form)).length : 0);
        writeUrl(controller);
    }

    Object.values(controllers).forEach((controller) => {
        if (controller.form) {
            controller.form.querySelectorAll("select[name], input[name]").forEach((el) => {
                el.addEventListener("change", () => {
                    controller.apply();
                    if (currentController() === controller) {
                        syncChrome();
                    }
                });
            });
        }
        controller.resetIds.forEach((id) => {
            const btn = document.getElementById(id);
            if (btn) {
                btn.addEventListener("click", (e) => {
                    e.preventDefault();
                    controller.reset();
                    if (currentController() === controller) {
                        syncChrome();
                    }
                });
            }
        });
    });

    if (sortSelect) {
        sortSelect.addEventListener("change", () => {
            const controller = currentController();
            if (controller) {
                controller.apply();
            }
            syncChrome();
        });
    }

    // Bind shown.bs.tab only to the card-tab triggers (the mobile dropdown proxies to the
    // desktop button): the login/register widget fires the same event against its own pane.
    document.querySelectorAll('[data-bs-toggle="tab"]').forEach((el) => {
        const target = (el.getAttribute("data-bs-target") || "").replace("#", "");
        if (!CARD_TAB_IDS.includes(target)) {
            return;
        }
        el.addEventListener("shown.bs.tab", () => {
            const controller = controllers[target] || null;
            if (controller) {
                controller.apply();
            }
            syncChrome(target);
        });
    });

    // ion.rangeSlider mis-measures inside a hidden modal; force a recalc on open.
    document.querySelectorAll('[id^="filters-modal-"]').forEach((modalEl) => {
        modalEl.addEventListener("shown.bs.modal", () => {
            window.dispatchEvent(new Event("resize"));
        });
    });

    let resizeTimer;
    window.addEventListener("resize", () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
            Object.values(controllers).forEach((controller) => controller.apply());
        }, 150);
    });

    Object.values(controllers).forEach((controller) => controller.apply());
    syncChrome();
    // Restore after the sqft sliders have initialized (their setup runs on
    // DOMContentLoaded), so handles can be moved. As a deferred module we execute during
    // "interactive" -- before DOMContentLoaded -- so wait for it unless the page is done.
    if (document.readyState === "complete") {
        restoreFromUrl();
    } else {
        document.addEventListener("DOMContentLoaded", restoreFromUrl);
    }
}

// Deferred module: the DOM is fully parsed by the time this runs, so build immediately.
initDetailFilters();
